<template>
  <template v-if="certificates !== null && !isLoading">
    <rbz-table
      :columns="columns"
      :table-data="certificates"
      :row-buttons="rowButtons"
      :row-classes="
        (item) => {
          return newCertificateId === item.id ? 'highlight-row' : '';
        }
      "
      :show-download-button="false"
      class="rbz-list-table"
      data-qa="server-certificates-table"
      :show-filter-row="true"
      :new-button-label="$t('certificate.serverToBackendCaCertificate.newCertificate')"
      new-button-classes="original-text"
      @new-button-clicked="openNewModal"
      @edit-row-button-clicked="openEditModal"
      @delete-row-button-clicked="openDeleteModal"
    >
      <template #tableResultsOverride="props">
        {{ $t('certificate.counter', props.numberOfResults) }}
      </template>
    </rbz-table>
    <div class="new-certificate-modal">
      <rbz-modal-dialog
        :show="showNewModal"
        :title="$t('certificate.serverToBackendCaCertificate.newModal.title')"
        :primary-btn-title="$t('common.operations.save.label')"
        @onPrimaryBtnClick="addNewCertificate"
        :secondary-btn-title="$t('common.operations.cancel.label')"
        @onSecondaryBtnClick="closeNewModal"
        @closeModal="closeNewModal"
        size="medium"
      >
        <template #content>
          <div class="rbz-field">
            <rbz-form-input
              name="certificate-name"
              variant="tertiary"
              class="certificate-name-input"
              data-qa="certificate-name-input"
              :label="$t('certificate.certificateName')"
              v-model:inputValue="newCertificateData.name"
              :placeholder="$t('certificate.certificateName')"
              :is-invalid="!!newCertificateNameError || !!newCertificateDuplicateError"
              :error-message-text="newCertificateNameError || newCertificateDuplicateError"
            >
              <template #postLabel>
                <div class="rbz-field-info">
                  <b-tooltip target="tooltip-certificate-name" triggers="hover click">
                    {{ $t('certificate.nameOptionalHelper') }}
                  </b-tooltip>
                  <icon-info-small
                    color="dusty-gray"
                    id="tooltip-certificate-name"
                    class="info-icon"
                  />
                </div>
              </template>
            </rbz-form-input>
          </div>
          <div class="rbz-field">
            <label class="rbz-label">
              {{ $t('certificate.certificateType') }}
            </label>
            <div class="certificate-type-wrapper">
              <rbz-form-radio
                id="certificate-type-manual"
                name="certificate-type"
                data-qa="certificate-type-manual-radio"
                :label="$t('certificate.radioManualInput')"
                @input="newCertificateData.isManualInput = 'manual'"
                :checked="newCertificateData.isManualInput === 'manual'"
              />
              <rbz-form-radio
                id="certificate-type-extraction"
                name="certificate-type"
                data-qa="certificate-type-extract-radio"
                :label="$t('certificate.radioExtractFile', { type: 'PEM' })"
                @input="newCertificateData.isManualInput = 'extract'"
                :checked="newCertificateData.isManualInput === 'extract'"
              />
            </div>
          </div>
          <layout-divider-line
            v-if="newCertificateData.isManualInput === 'manual'"
            color="fun-blue"
            divider-type="bold"
            class="certificate-details-divider"
          />
          <div v-if="newCertificateData.isManualInput === 'manual'">
            <span class="rbz-label">
              {{ $t('certificate.modalManualTitle') }}
            </span>
          </div>
          <div v-else>
            <span class="rbz-label">
              {{ $t('certificate.modalExtractionTitle') }}
            </span>
            <div class="rbz-field">
              <div class="file has-name">
                <label class="file-label">
                  <input
                    class="file-input"
                    type="file"
                    @input="
                      loadFile($event);
                      extractFile();
                    "
                    name="certFileInput"
                    data-qa="file-input"
                    accept=".pem"
                  />
                  <span class="file-cta">
                    <span class="file-icon">
                      <icon-upload />
                    </span>
                    <span class="file-label">
                      {{ $t('certificate.chooseFile', { type: 'PEM' }) }}
                    </span>
                  </span>
                  <span class="file-name">
                    {{ newCertificateData.file?.name || $t('common.noFileChosen') }}
                  </span>
                </label>
              </div>
            </div>
          </div>
          <layout-divider-line
            v-if="newCertificateData.isManualInput === 'extract'"
            color="fun-blue"
            divider-type="bold"
            class="certificate-details-divider"
          />
          <div class="rbz-field">
            <rbz-form-textarea
              name="certificate-body-textarea"
              :label="$t('certificate.certificateBody')"
              v-model:inputValue="newCertificateData.certificate"
              :placeholder="$t('certificate.certificateBodyPlaceholder')"
              rows="6"
              class="certificate-body-textarea"
              data-qa="certificate-body-textarea"
            />
          </div>
        </template>
      </rbz-modal-dialog>
    </div>
    <div class="edit-certificate-modal">
      <rbz-modal-dialog
        :show="showEditModal"
        :title="$t('certificate.serverToBackendCaCertificate.editModal.title')"
        :subtitle="selectedCertificate?.name"
        :primary-btn-title="$t('common.operations.save.label')"
        @onPrimaryBtnClick="editCertificate"
        :secondary-btn-title="$t('common.operations.cancel.label')"
        @onSecondaryBtnClick="closeEditModal"
        @closeModal="closeEditModal"
        size="medium"
      >
        <template #content>
          <div class="rbz-field">
            <rbz-form-input
              name="certificate-name-edit"
              variant="tertiary"
              class="certificate-name-edit-input"
              data-qa="certificate-name-edit-input"
              :label="$t('certificate.certificateName')"
              v-model:inputValue="selectedCertificate.name"
              :placeholder="$t('certificate.certificateName')"
              :is-invalid="!!editCertificateNameError || !!editCertificateDuplicateError"
              :error-message-text="editCertificateNameError || editCertificateDuplicateError"
            >
              <template #postLabel>
                <div class="rbz-field-info">
                  <b-tooltip target="tooltip-certificate-name-edit" triggers="hover click">
                    {{ $t('certificate.nameOptionalHelper') }}
                  </b-tooltip>
                  <icon-info-small
                    color="dusty-gray"
                    id="tooltip-certificate-name-edit"
                    class="info-icon"
                  />
                </div>
              </template>
            </rbz-form-input>
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="connected-backend-services"
              class="connected-backend-services"
              data-qa="connected-backend-services-input"
              :label="$t('certificate.connectedBackendServices')"
              :inputValue="connectedBackendServices"
              disabled
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="certificate-subject"
              class="certificate-subject"
              data-qa="certificate-subject-input"
              :label="$t('certificate.certificateSubject')"
              :inputValue="selectedCertificate.subject"
              disabled
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="certificate-issuer"
              class="certificate-issuer"
              data-qa="certificate-issuer-input"
              :label="$t('certificate.issuer')"
              :inputValue="selectedCertificate.issuer"
              disabled
            />
          </div>
          <div class="rbz-field">
            <rbz-form-input
              name="san"
              class="certificate-san"
              data-qa="san-input"
              :label="$t('certificate.san')"
              :inputValue="selectedCertificate.san.toString()"
              disabled
            />
          </div>
          <div class="rbz-field">
            <rbz-form-textarea
              name="certificate-body-textarea"
              :label="$t('certificate.certificateBody')"
              :inputValue="selectedCertificate['cert_body']"
              class="certificate-body-textarea"
              data-qa="certificate-body-textarea"
              disabled
            />
          </div>
          <certificates-tabs
            :selected-items="selectedApps"
            :all-items="backendServices"
            side="backendCA"
            :assigned-certificates="assignedCertificates"
            :selected-certificate="selectedCertificate"
            @update-selected-apps="updateSelectedApps"
            @update-replace-certificate="updateReplaceCertificate"
            ref="certificatesTabsRef"
          >
          </certificates-tabs>
        </template>
      </rbz-modal-dialog>
    </div>
    <certificates-list-delete-modal
      :selected-certificate="selectedCertificate"
      side="backendCA"
      :attached-apps="attachedApps"
      :entity-name="entityName"
      @delete-certificate="deleteCertificate"
      ref="certificatesDeleteModalRef"
    >
    </certificates-list-delete-modal>
  </template>
  <rbz-loader :loading="!!isLoading" :data-exists="certificates !== null" height="100%" />
</template>
<script lang="ts">
import _ from 'lodash';
import { defineComponent } from 'vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { GenericObject, BackendService, ServerToBackendCaCertificate } from '@/types';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RequestsUtils from '@/helpers/RequestsUtils';
import { useBranchesStore } from '@/store/BranchesStore';
import { mapStores } from 'pinia';
import RbzFormRadio from '@/components/RbzFormRadio.vue';
import RbzFormTextarea from '@/components/RbzFormTextarea.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import RbzLoader from '@/components/RbzLoader.vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import CertificatesListDeleteModal from '@/components/CertificatesListDeleteModal.vue';
import CertificatesTabs from '@/components/CertificatesTabs.vue';
import IconUpload from 'ui-component-library/assets/icons/IconUpload.vue';
import IconInfoSmall from 'ui-component-library/assets/icons/IconInfoSmall.vue';

export default defineComponent({
  name: 'CertificatesListServerToBackendCaTab',
  components: {
    IconInfoSmall,
    IconUpload,
    CertificatesTabs,
    CertificatesListDeleteModal,
    RbzLoader,
    LayoutDividerLine,
    RbzFormInput,
    RbzFormRadio,
    RbzFormTextarea,
    RbzModalDialog,
    RbzTable,
  },
  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if ((this.$route.name as string).includes('Certificates') && val && val !== oldVal) {
          this.setPageLoadingStatus(true);
          await this.loadData();
          this.setPageLoadingStatus(false);
        }
      },
      immediate: true,
    },
    assignedApps: {
      handler: function (val) {
        this.selectedApps = _.cloneDeep(val);
      },
      deep: true,
      immediate: true,
    },
  },
  data() {
    const defaultNewCertificateData = {
      isManualInput: 'manual',
      file: null,
      certificate: '',
      name: '',
    };
    return {
      columns: [
        {
          id: 'name',
          title: this.$t('certificate.name'),
          fieldNames: ['name'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'white-space-pre-line word-break-break-word',
          autoFocus: true,
        },
        {
          id: 'expiration-date',
          title: this.$t('certificate.expirationDate'),
          fieldNames: ['exp_date'],
          isSortable: true,
          isSearchable: true,
          classes: 'width-140px',
          cellContentClasses: 'ellipsis',
          cellContentConditionalClasses: (item: ServerToBackendCaCertificate) => {
            const sslDateUnix = Number(new Date(`${item['exp_date']}T00:00:00.000Z`));
            const currentDateUnix = Number(new Date());
            const ONE_MONTH = 1000 * 60 * 60 * 24 * 30;
            return sslDateUnix - currentDateUnix < ONE_MONTH ? 'has-text-danger' : '';
          },
        },
        {
          id: 'linked-to',
          title: this.$t('certificate.linkedTo'),
          displayFunction: (item: ServerToBackendCaCertificate) => {
            const matchingBackendServices = _.filter(
              this.backendServices,
              (backendService: BackendService) => {
                return backendService['mtls_trusted_certificate'] === item.id;
              }
            );
            return matchingBackendServices.length
              ? _.map(matchingBackendServices, 'name').join('\n')
              : '';
          },
          isSearchable: true,
          classes: 'width-140px',
          cellContentClasses: 'multi-line white-space-pre-line',
        },
        {
          id: 'san',
          title: this.$t('certificate.san'),
          fieldNames: ['san'],
          displayFunction: (item) => {
            return item.san?.join('\n') || '';
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-280px',
          cellContentClasses: 'white-space-pre-line word-break-break-word',
        },
        {
          id: 'issuer',
          title: this.$t('certificate.issuer'),
          fieldNames: ['issuer'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'white-space-pre-line word-break-break-word',
        },
      ] as RbzTableColumnOptions[],
      rowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
        },
        {
          id: 'delete',
          icon: 'IconDelete',
          disabledCallback: this.isDeleteDisabled,
          tooltipCallback: this.deleteTooltip,
        },
      ] as RbzTableRowButton[],
      certificates: null as ServerToBackendCaCertificate[],
      backendServices: null as BackendService[],
      pageLoadingCounter: null,
      showNewModal: false,
      showEditModal: false,
      defaultNewCertificateData: _.cloneDeep(defaultNewCertificateData),
      newCertificateData: _.cloneDeep(defaultNewCertificateData),
      selectedCertificate: null as ServerToBackendCaCertificate,
      selectedApps: [] as BackendService[],
      attachedApps: [] as string[],
      replaceCertificate: null as ServerToBackendCaCertificate['id'],
      newCertificateId: '',
      newCertificateNameError: '',
      editCertificateNameError: '',
      newCertificateDuplicateError: '',
      editCertificateDuplicateError: '',
      certificatesInUse: [] as { name: string; id: string; side: string }[],
    };
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore),

    entityName() {
      return {
        plural: this.$t('certificate.serverToBackendCaCertificate.plural'),
        singular: this.$t('certificate.serverToBackendCaCertificate.singular'),
      };
    },

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },

    backendServicesByCertNameMap(): GenericObject {
      const returnValue: { [key: string]: string[] } = {};
      this.backendServices.forEach((backendService: BackendService) => {
        const certId = backendService['mtls_trusted_certificate'];
        if (certId) {
          if (!returnValue[certId]) {
            returnValue[certId] = [];
          }
          returnValue[certId].push(backendService.name);
        }
      });
      return returnValue;
    },

    connectedBackendServices(): string {
      const backendServices = this.backendServicesByCertNameMap[this.selectedCertificate.id];
      return backendServices?.join(', ') || '';
    },

    assignedApps(): BackendService[] {
      return (
        _.filter(this.backendServices, (backendService: BackendService) => {
          return backendService?.['mtls_trusted_certificate'] === this.selectedCertificate?.id;
        }) || []
      );
    },

    assignedCertificates(): ServerToBackendCaCertificate[] {
      const balancerAssignedCertificates: ServerToBackendCaCertificate[] = [];
      const backendServicesAssignedCertificates: ServerToBackendCaCertificate[] = [];
      _.forEach(this.backendServices, (backendService: BackendService) => {
        const certificate = _.find(this.certificates, (certificate) => {
          return certificate.id === backendService['mtls_trusted_certificate'];
        });
        if (certificate) {
          backendServicesAssignedCertificates.push(certificate);
        }
      });
      return _.union(balancerAssignedCertificates, backendServicesAssignedCertificates);
    },
  },
  methods: {
    validateCertificateName(name: string): boolean {
      const nameRegex = /^[a-zA-Z0-9_-]*$/;
      if (name && !nameRegex.test(name)) {
        return false;
      }
      return true;
    },

    getCertificateTypeByName(name: string, excludeId?: string): string {
      if (!name || !this.certificatesInUse) return '';
      const certType = this.certificatesInUse
        .filter((cert) => cert.id !== excludeId)
        .find((cert) => cert.name?.toLowerCase() === name.toLowerCase())?.side;
      return certType || '';
    },

    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    async loadData() {
      this.setPageLoadingStatus(true);
      const certificatesPromise = this.loadCertificates();
      const backendServicesPromise = this.loadBackendServices();
      await Promise.all([certificatesPromise, backendServicesPromise]);
      this.setPageLoadingStatus(false);
    },

    async loadCertificates() {
      this.setPageLoadingStatus(true);
      const url = `conf/${this.selectedBranch}/certificates`;
      const certificatesResponse = await RequestsUtils.sendRequest({
        methodName: 'GET',
        url,
      });
      this.certificates = certificatesResponse?.data?.items || [];
      this.certificatesInUse = this.certificates.map((certificate) => {
        return { name: certificate.name, id: certificate.id, side: certificate.side };
      });
      this.certificates = _.filter(
        this.certificates,
        (certificate: ServerToBackendCaCertificate) => {
          return certificate.side === 'backendCA';
        }
      );
      this.setPageLoadingStatus(false);
    },

    async loadBackendServices() {
      this.setPageLoadingStatus(true);
      const url = `conf/${this.selectedBranch}/backend-services`;
      const backendServicesResponse = await RequestsUtils.sendRequest({
        methodName: 'GET',
        url,
      });
      this.backendServices = backendServicesResponse?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    isDeleteDisabled(certificate: ServerToBackendCaCertificate) {
      return this.isCertificateReferenced(certificate);
    },

    isCertificateReferenced(certificate: ServerToBackendCaCertificate): boolean {
      return this.backendServices.some((backendService: BackendService) => {
        return backendService['mtls_trusted_certificate'] === certificate.id;
      });
    },

    deleteTooltip(certificate: ServerToBackendCaCertificate): string {
      if (this.isCertificateReferenced(certificate)) {
        return this.$t('common.operations.delete.disabledTooltip.referenced', this.entityName);
      }
      return this.$t('common.operations.delete.tableTooltip');
    },

    // New certificate modal
    openNewModal() {
      this.showNewModal = true;
    },

    closeNewModal() {
      this.showNewModal = false;
      this.newCertificateData = _.cloneDeep(this.defaultNewCertificateData);
      this.newCertificateNameError = '';
      this.newCertificateDuplicateError = '';
    },

    loadFile(event: Event) {
      const target = event.target as HTMLInputElement;
      const file: File = target.files[0];
      const extension = file?.name?.split('.')?.pop();
      this.newCertificateData.file = file;
      this.newCertificateData.certificate = '';
    },

    async extractFile() {
      const url = 'tools/certificates/extractpem';
      const formData = new FormData();
      formData.append('fileName', this.newCertificateData.file, this.newCertificateData.file.name);
      const failureMessage = this.$t('certificate.extractFailure', { type: 'PEM' });
      const response = await RequestsUtils.sendRequest({
        methodName: 'POST',
        url,
        data: formData,
        config: { headers: { 'Content-Type': 'multipart/form-data' } },
        failureMessage,
      });
      if (response?.data) {
        this.newCertificateData.certificate = response.data['certificate_body'] || '';
      }
    },

    newCertificate(): ServerToBackendCaCertificate {
      const factory = DatasetsUtils.newEntityFactory['server-to-backend-ca-certificates'];
      return factory && factory();
    },

    async addNewCertificate() {
      const name = this.newCertificateData.name?.trim() || '';
      if (!this.validateCertificateName(name)) {
        this.newCertificateNameError = this.$t('certificate.nameValidationError');
        return;
      }
      this.newCertificateNameError = '';
      const certType = this.getCertificateTypeByName(name);
      if (certType !== '') {
        this.newCertificateDuplicateError = this.$t('certificate.nameDuplicateError', {
          name,
          type: certType,
        });
        return;
      }
      this.newCertificateDuplicateError = '';
      this.setPageLoadingStatus(true);
      const certificateToAdd = this.newCertificate();
      const trimmedName = (this.newCertificateData.name || '').trim();
      if (trimmedName !== '') {
        certificateToAdd.name = trimmedName;
      }
      certificateToAdd['cert_body'] = this.newCertificateData.certificate;
      const successMessage = this.$t('common.operations.new.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.new.messages.failure', this.entityName);
      const response = await RequestsUtils.sendRequest({
        methodName: 'POST',
        url: `conf/${this.selectedBranch}/certificates/${certificateToAdd.id}`,
        data: certificateToAdd,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.newCertificateId = certificateToAdd.id;
      }
      this.closeNewModal();
      await this.loadData();
      this.setPageLoadingStatus(false);
    },

    // Edit certificate modal
    openEditModal(certificate: ServerToBackendCaCertificate) {
      this.selectedCertificate = _.cloneDeep(certificate);
      this.showEditModal = true;
    },

    closeEditModal() {
      this.showEditModal = false;
      this.editCertificateNameError = '';
      this.editCertificateDuplicateError = '';
    },

    updateSelectedApps(newAppsIds: BackendService['id'][]) {
      this.selectedApps = _.filter(this.backendServices, (backendService) => {
        return newAppsIds.includes(backendService.id);
      });
    },

    updateReplaceCertificate(id: ServerToBackendCaCertificate['id'][]) {
      this.replaceCertificate = id;
    },

    async editCertificate() {
      const name = this.selectedCertificate.name?.trim() || '';
      if (!this.validateCertificateName(name)) {
        this.editCertificateNameError = this.$t('certificate.nameValidationError');
        return;
      }
      this.editCertificateNameError = '';
      const certType = this.getCertificateTypeByName(name, this.selectedCertificate.id);
      if (certType !== '') {
        this.editCertificateDuplicateError = this.$t('certificate.nameDuplicateError', {
          name,
          type: certType,
        });
        return;
      }
      this.editCertificateDuplicateError = '';
      this.setPageLoadingStatus(true);
      const backendServicesToRemove = _.differenceBy(this.assignedApps, this.selectedApps, 'id');
      const backendServicesToAdd = _.differenceBy(this.selectedApps, this.assignedApps, 'id');
      const methodName = 'PUT';
      let urlArgs = '';
      for (const backendService of backendServicesToRemove) {
        const url = `conf/${this.selectedBranch}/backend-services/${backendService.id}`;
        backendService['mtls_trusted_certificate'] = '';
        await RequestsUtils.sendRequest({
          methodName: methodName,
          url,
          data: backendService,
        });
      }
      for (const backendService of backendServicesToAdd) {
        const url = `conf/${this.selectedBranch}/backend-services/${backendService.id}`;
        backendService['mtls_trusted_certificate'] = this.selectedCertificate.id;
        await RequestsUtils.sendRequest({
          methodName: methodName,
          url,
          data: backendService,
        });
      }
      const certificateRenew = this.selectedCertificate['le_auto_renew'];
      const certificateReplace = this.selectedCertificate['le_auto_replace'];
      const nameParam = encodeURIComponent(this.selectedCertificate.name || '');
      urlArgs = `?name=${nameParam}&le_auto_renew=${certificateRenew}&le_auto_replace=${certificateReplace}`;
      if (this.replaceCertificate) {
        urlArgs = `${urlArgs}&replace_cert_id=${this.replaceCertificate}`;
        this.replaceCertificate = null;
      }
      const url = `conf/${this.selectedBranch}/certificates/${this.selectedCertificate.id}${urlArgs}`;
      const successMessage = this.$t(
        'certificate.serverToBackendCaCertificate.editModal.attachSuccess'
      );
      const data = this.selectedCertificate;
      await RequestsUtils.sendRequest({
        methodName: methodName,
        url,
        data,
        successMessage,
      });
      this.closeEditModal();
      await this.loadData();
      this.setPageLoadingStatus(false);
    },

    // Delete certificate modal
    openDeleteModal(certificate: ServerToBackendCaCertificate) {
      this.selectedCertificate = _.cloneDeep(certificate);
      this.attachedApps = this.backendServicesByCertNameMap[certificate.id] || [];
      this.$refs.certificatesDeleteModalRef.openDeleteModal();
    },

    async deleteCertificate() {
      this.setPageLoadingStatus(true);
      await this.$refs.certificatesDeleteModalRef.deleteCertificate();
      await this.loadData();
      this.setPageLoadingStatus(false);
    },
  },
});
</script>
<style scoped lang="scss">
@use 'sass:map';
@use 'ui-component-library/assets/styles/colors' as colors;

.certificate-type-wrapper {
  display: flex;
  gap: 16px;
}

:deep(.rbz-table .data-row) {
  &.highlight-row {
    background-color: map.get(colors.$colors, 'ice-blue');
  }
}

:deep(.rbz-table .multi-line) {
  -webkit-line-clamp: 10;
}

.attached-apps {
  margin-top: 16px;
}

.certificate-details-divider {
  margin: 20px 0;
}
</style>
